import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import publicService from '../services/publicService';
import FormField from '../components/common/FormField';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatDate } from '../utils/formatters';
import { validateEmail } from '../utils/validators';
import { ROUTES } from '../utils/constants';
import './ApplicantPages.css';

// The shareable link / QR code for one property. We email a personal link, so nobody can start
// an application in someone else's name.
const ApplyToPropertyPage = () => {
  const { token } = useParams();
  const [property, setProperty] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [values, setValues] = useState({ email: '', first_name: '', last_name: '', mobile: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    publicService
      .getApplyLink(token)
      .then(setProperty)
      .catch(() => setLoadError('This application link is not active. Please ask the landlord or agent for a new one.'));
  }, [token]);

  const handleChange = (e) => setValues((v) => ({ ...v, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailError = validateEmail(values.email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }
    setErrors({});
    setError('');
    setSubmitting(true);
    try {
      await publicService.startFromApplyLink(token, {
        email: values.email,
        first_name: values.first_name || undefined,
        last_name: values.last_name || undefined,
        mobile: values.mobile || undefined
      });
      setSent(true);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        setErrors(Object.fromEntries(Object.entries(apiErrors).map(([k, v]) => [k, v[0]])));
      } else if (err.response?.status === 429) {
        setError('Too many attempts. Please wait a minute and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ap-center">
      <div className="ap-card ap-card--narrow">
        <h1 className="ap-brand"><Link to={ROUTES.HOME}>TenantVerify SA</Link></h1>

        {loadError && <Alert type="error" message={loadError} />}
        {!property && !loadError && <LoadingSpinner />}

        {property && !sent && (
          <>
            <h2>Apply to rent</h2>
            <dl className="ap-facts">
              <dt>Property</dt><dd>{property.address}</dd>
              <dt>Monthly rent</dt><dd>{property.monthly_rent}</dd>
              <dt>Available from</dt><dd>{formatDate(property.occupation_date)}</dd>
              <dt>Offered by</dt><dd>{property.organisation_name}</dd>
            </dl>
            {property.authority_verified ? (
              <p className="apply-trust apply-trust--yes">
                <strong>Verified:</strong> we have checked that {property.organisation_name} may let this property.
              </p>
            ) : (
              <p className="apply-trust">
                We have not yet verified that {property.organisation_name} may let this property. You choose what to share, and you
                see who is asking before you agree to anything.
              </p>
            )}
            <p className="ap-muted">
              Enter your email address and we will send you a personal link to start. You will see exactly what is
              checked, and why, before you agree to anything. No password is needed.
            </p>

            {error && <Alert type="error" message={error} />}

            <form onSubmit={handleSubmit} noValidate>
              <FormField label="Email address" name="email" type="email" value={values.email} onChange={handleChange} error={errors.email} required autoComplete="email" />
              <div className="ap-row">
                <FormField label="First name" name="first_name" value={values.first_name} onChange={handleChange} error={errors.first_name} autoComplete="given-name" />
                <FormField label="Last name" name="last_name" value={values.last_name} onChange={handleChange} error={errors.last_name} autoComplete="family-name" />
              </div>
              <FormField label="Mobile number (optional)" name="mobile" type="tel" value={values.mobile} onChange={handleChange} error={errors.mobile} placeholder="0821234567" autoComplete="tel" />
              <button type="submit" className="btn-primary ap-button" disabled={submitting}>
                {submitting ? 'Sending...' : 'Email me my link'}
              </button>
            </form>
          </>
        )}

        {sent && (
          <>
            <h2>Check your email</h2>
            <p>
              If that address can receive email, we have sent a personal link to <strong>{values.email}</strong>.
              Open it to start your application. It can take a few minutes, and it is worth checking your spam folder.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyToPropertyPage;
